import { chakra, Flex, keyframes, Text } from '@chakra-ui/react';
import { Keyframes } from '@emotion/react';
import { isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { LogoSpinner } from '~/common/components/LogoSpinner/LogoSpinner';
import { ValidationTag } from '~/common/components/ValidationTag';
import { OFFER_SCORE_CHART_STYLES } from './constants';
import { getStickLocationPadding } from './helpers';
// Create a function to generate keyframes for the score animation
const createScoreAnimation = (
  startScore: number,
  endScore: number,
  size: 'sm' | 'md' | 'lg' = 'md'
) => {
  const steps = 20; // Number of steps in the animation
  const keyframeSteps: string[] = [];

  for (let i = 0; i <= steps; i++) {
    const progress = i / steps;
    const currentScore = startScore + (endScore - startScore) * progress;
    const percentage = currentScore / 10;
    const angleInRadians = percentage * Math.PI;

    const { bottomPadding, leftPadding } = getStickLocationPadding(size);

    const leftRatio = (1 - Math.cos(angleInRadians)) / 2;
    const bottomRatio = Math.sin(angleInRadians);
    const left = `calc(${leftRatio * 100}% - ${
      leftPadding + bottomPadding * leftRatio
    }px)`;
    const bottom = `calc(${bottomRatio * 100}% - ${
      bottomRatio * bottomPadding
    }px)`;
    const rotation = percentage * 180;

    keyframeSteps.push(`${(progress * 100).toFixed(0)}% {
      left: ${left};
      bottom: ${bottom};
      transform: rotate(${rotation}deg);
    }`);
  }

  return keyframes`${keyframeSteps.join('\n')}`;
};

export const OfferScoreChart = ({
  size = 'md',
  score,
  loading,
  maskColor,
}: {
  score?: number;
  loading?: boolean;
  maskColor?: string;
  size?: 'sm' | 'md' | 'lg';
}) => {
  const [localScore, setLocalScore] = useState<number | undefined>(score);
  const [prevScore, setPrevScore] = useState<number | undefined>(score);
  const [prevScoreDifference, setPrevScoreDifference] = useState<
    number | undefined
  >(undefined);
  const [showDifference, setShowDifference] = useState(false);
  const [animation, setAnimation] = useState<Keyframes | undefined>(undefined);
  const [scoreLeft, setScoreLeft] = useState<string | undefined>(undefined);
  const [scoreBottom, setScoreBottom] = useState<string | undefined>(undefined);
  const [scoreAngle, setScoreAngle] = useState<number | undefined>(undefined);

  const styles = OFFER_SCORE_CHART_STYLES[size];

  const { bottomPadding, leftPadding } = getStickLocationPadding(size);

  useEffect(() => {
    if (score !== undefined && score !== prevScore) {
      const newAnimation = createScoreAnimation(prevScore || 0, score, size);
      setAnimation(newAnimation);
      setPrevScoreDifference(score - (prevScore || 0));
      setTimeout(() => {
        setPrevScore(score);
        setAnimation(undefined);
        setShowDifference(true);
        setTimeout(() => {
          setShowDifference(false);
        }, 2000);
      }, 1000);
    }
    const scorePercentage = (score || 0) / 10;
    const scoreAngle = scorePercentage * 180;

    const scoreBottomRatio = Math.sin(scorePercentage * Math.PI);

    // For a proper half-circle motion from left to right:
    const scoreLeftRatio = (1 - Math.cos(scorePercentage * Math.PI)) / 2;
    const scoreLeft = `calc(${scoreLeftRatio * 100}% - ${
      leftPadding + bottomPadding * scoreLeftRatio
    }px)`;

    const scoreBottom = `calc(${scoreBottomRatio * 100}% - ${
      scoreBottomRatio * bottomPadding
    }px)`;
    setScoreLeft(scoreLeft);
    setScoreBottom(scoreBottom);
    setScoreAngle(scoreAngle);
    setLocalScore(score);
  }, [score, prevScore, bottomPadding, leftPadding, size]);

  return (
    <Background {...styles.background}>
      <Mask backgroundColor={maskColor} {...styles.mask}>
        <Flex
          alignItems="baseline"
          position="relative"
          {...styles.textContainer}
        >
          {!loading &&
            (!isNil(score) ? (
              <>
                {size !== 'sm' && ( // Doesn't fit on small size
                  <ValidationTag
                    backgroundColor={
                      !!prevScoreDifference && prevScoreDifference > 0
                        ? 'rgba(32, 89, 61, 0.40)'
                        : 'rgba(114, 35, 42, 0.40)'
                    }
                    isError={!!prevScoreDifference && prevScoreDifference < 0}
                    isSuccess={!!prevScoreDifference && prevScoreDifference > 0}
                    opacity={showDifference ? 1 : 0}
                    position="absolute"
                    right="0"
                    size="sm"
                    top="2"
                    transition="opacity 0.5s"
                  >
                    {prevScoreDifference && prevScoreDifference < 0 ? '-' : '+'}
                    {prevScoreDifference && Math.abs(prevScoreDifference)}
                  </ValidationTag>
                )}
                <Text
                  color={animation ? 'whiteAlpha.600' : 'white'}
                  {...styles.scoreText}
                >
                  {animation ? prevScore || 0 : localScore}
                </Text>
                <Text color="whiteAlpha.600" {...styles.scoreTextSmall}>
                  / 10
                </Text>
              </>
            ) : (
              <Text alignSelf="end" color="whiteAlpha.600" {...styles.tbdText}>
                TBD
              </Text>
            ))}
          {!!loading && (
            <Flex
              alignItems="center"
              direction="column"
              height="100%"
              justifyContent="flex-end"
            >
              <LogoSpinner height="15px" width="15px" />
              {size !== 'sm' && (
                <Text color="whiteAlpha.600" fontWeight="medium">
                  Calculating
                </Text>
              )}
            </Flex>
          )}
        </Flex>
      </Mask>
      {!isNil(score) && !loading && (
        <Flex
          {...styles.stick}
          animation={animation ? `${animation} 1s forwards` : undefined}
          background="white"
          position="absolute"
          // If no animation is set, use the calculated position
          {...(!animation && {
            bottom: scoreBottom,
            left: scoreLeft,
            transform: `rotate(${scoreAngle}deg)`,
          })}
        />
      )}
    </Background>
  );
};

export const Background = chakra(Flex, {
  baseStyle: {
    position: 'relative',
    height: '80px',
    background:
      'linear-gradient(90deg, rgba(255,79,93,1) 0%, rgba(255,203,0,1) 50%, rgba(71,198,137,1) 100%);',
    borderRadius: '150px 150px 0 0',
    textAlign: 'center',
    width: '150px',
    minWidth: '150px',
  },
});

export const Mask = chakra(Flex, {
  baseStyle: {
    position: 'absolute',
    height: 'calc(100% - 10px)',
    width: '130px',
    minWidth: '130px',
    backgroundColor: 'bg.main',
    borderRadius: '150px 150px 0 0',
    top: '10px',
    left: '10px',
    alignItems: 'bottom',
    justifyContent: 'center',
  },
});
